/**
 * AddExchangeForm
 * Connects a new exchange account with encrypted credentials.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLingui } from '@lingui/react';
import { useApiClient } from '../api/client';
import type { Exchange } from '../api/client';

type TestStatus = 'idle' | 'testing' | 'ok' | 'error';

export default function AddExchangeForm() {
  const { _ } = useLingui();
  const api = useApiClient();
  const navigate = useNavigate();
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [selectedExchange, setSelectedExchange] = useState<Exchange | null>(null);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<TestStatus>('idle');
  const [testError, setTestError] = useState<string | null>(null);

  useEffect(() => {
    // GET /api/exchanges/catalog — returns active exchanges, no admin role needed
    void api
      .listExchangeCatalog()
      .then(setExchanges)
      .finally(() => setLoadingCatalog(false));
  }, [api]);

  function handleSelectExchange(id: string) {
    const ex = exchanges.find((e) => e.id === id) ?? null;
    setSelectedExchange(ex);
    setCredentials({});
    setTestStatus('idle');
    setTestError(null);
  }

  function handleCredentialChange(field: string, value: string) {
    setCredentials((prev) => ({ ...prev, [field]: value }));
    // Credential değişince önceki test sonucu geçersiz
    setTestStatus('idle');
    setTestError(null);
  }

  async function handleTestConnection() {
    if (!selectedExchange) return;
    setTestStatus('testing');
    setTestError(null);
    try {
      const result = await api.testExchangeConnection({
        exchangeId: selectedExchange.id,
        credentials,
      });
      if (result.ok) {
        setTestStatus('ok');
      } else {
        setTestStatus('error');
        setTestError(result.error ?? _({ id: 'exchanges.addForm.error.testFailed' }));
      }
    } catch (err) {
      setTestStatus('error');
      setTestError(
        err instanceof Error ? err.message : _({ id: 'exchanges.addForm.error.testError' }),
      );
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedExchange) return;
    setSaving(true);
    setError(null);
    try {
      await api.connectExchange({
        exchangeId: selectedExchange.id,
        label: label.trim() || undefined,
        credentials,
      });
      void navigate('/exchanges');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : _({ id: 'exchanges.addForm.error.connectionFailed' }),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-lg mx-auto">
      <div className="mb-4">
        <h1 className="text-xl font-bold hidden lg:block">
          {_({ id: 'exchanges.addForm.title' })}
        </h1>
        <p className="text-sm text-base-content/60 mt-1">
          {_({ id: 'exchanges.addForm.subtitle' })}
        </p>
      </div>

      {error && (
        <div className="alert alert-error mb-4">
          <span>{error}</span>
        </div>
      )}

      {loadingCatalog ? (
        <div className="flex justify-center py-10">
          <span className="loading loading-spinner loading-lg" />
        </div>
      ) : (
        <form onSubmit={(e) => void handleSubmit(e)} className="card bg-base-100 shadow">
          <div className="card-body space-y-4">
            {/* Exchange selector */}
            <label className="form-control w-full">
              <div className="label">
                <span className="label-text font-medium">
                  {_({ id: 'exchanges.addForm.selectLabel' })}
                </span>
              </div>
              <select
                required
                className="select select-bordered w-full"
                value={selectedExchange?.id ?? ''}
                onChange={(e) => handleSelectExchange(e.target.value)}
              >
                <option value="" disabled>
                  {_({ id: 'exchanges.addForm.selectPlaceholder' })}
                </option>
                {exchanges.map((ex) => (
                  <option key={ex.id} value={ex.id}>
                    {ex.name}
                  </option>
                ))}
              </select>
            </label>

            {/* Label */}
            <label className="form-control w-full">
              <div className="label">
                <span className="label-text">
                  {_({ id: 'exchanges.addForm.labelField.label' })}
                </span>
                <span className="label-text-alt">
                  {_({ id: 'exchanges.addForm.labelField.hint' })}
                </span>
              </div>
              <input
                className="input input-bordered w-full"
                placeholder={_({ id: 'exchanges.addForm.labelField.placeholder' })}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </label>

            {/* Dynamic credential fields */}
            {selectedExchange && (
              <div className="space-y-3">
                <div className="divider text-xs">
                  {_({ id: 'exchanges.addForm.credentials.divider' })}
                </div>
                {selectedExchange.requiredCredentials.map((field) => (
                  <label key={field} className="form-control w-full">
                    <div className="label">
                      <span className="label-text font-mono">{field}</span>
                    </div>
                    <input
                      required
                      type="password"
                      className="input input-bordered w-full font-mono"
                      placeholder={_({
                        id: 'exchanges.addForm.credentials.fieldPlaceholder',
                        values: { field },
                      })}
                      value={credentials[field] ?? ''}
                      onChange={(e) => handleCredentialChange(field, e.target.value)}
                    />
                  </label>
                ))}
                {selectedExchange.apiDocUrl && (
                  <a
                    href={selectedExchange.apiDocUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link link-primary text-xs"
                  >
                    📖 {selectedExchange.name}{' '}
                    {_({ id: 'exchanges.addForm.credentials.docLinkSuffix' })}
                  </a>
                )}

                {/* Connection test */}
                <div className="pt-1">
                  <button
                    type="button"
                    className={`btn btn-sm w-full ${
                      testStatus === 'ok'
                        ? 'btn-success'
                        : testStatus === 'error'
                          ? 'btn-error'
                          : 'btn-outline'
                    }`}
                    disabled={
                      testStatus === 'testing' ||
                      selectedExchange.requiredCredentials.some((f) => !credentials[f])
                    }
                    onClick={() => void handleTestConnection()}
                  >
                    {testStatus === 'testing' ? (
                      <>
                        <span className="loading loading-spinner loading-xs" />{' '}
                        {_({ id: 'exchanges.addForm.test.testing' })}
                      </>
                    ) : testStatus === 'ok' ? (
                      _({ id: 'exchanges.addForm.test.success' })
                    ) : testStatus === 'error' ? (
                      _({ id: 'exchanges.addForm.test.retry' })
                    ) : (
                      _({ id: 'exchanges.addForm.test.default' })
                    )}
                  </button>
                  {testStatus === 'error' && testError && (
                    <p className="text-error text-xs mt-1">{testError}</p>
                  )}
                </div>
              </div>
            )}

            <div className="card-actions justify-between pt-2">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => void navigate('/exchanges')}
              >
                {_({ id: 'exchanges.addForm.actions.cancel' })}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!selectedExchange || saving || testStatus !== 'ok'}
                title={
                  testStatus !== 'ok'
                    ? _({ id: 'exchanges.addForm.actions.testRequired' })
                    : undefined
                }
              >
                {saving ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  _({ id: 'exchanges.addForm.actions.submit' })
                )}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
